import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Markdown from 'react-markdown';
import { Select, Tag } from 'antd';
import apiClient from '../../utils/axios';
import { useAuthCheck } from '../../hooks/useAuth';
import ContactLifecycleView from '../lifecycle/ContactLifecycleView';

function TimelineListPage() {
  // Check authentication before rendering
  useAuthCheck();
  const [contactId, setContactId] = useState('');
  const [timelineData, setTimelineData] = useState(null);
  const [error, setError] = useState(null);
  const [sortBy] = useState('timestamp'); // timestamp, source, type
  const [sortOrder] = useState('desc'); // asc, desc
  const [filterTypeId, setFilterTypeId] = useState([]);
  const [filterEventType, setFilterEventType] = useState([]);
  const [filterContentStatus, setFilterContentStatus] = useState([]);
  const [expandedEvents, setExpandedEvents] = useState(new Set());
  const [insights, setInsights] = useState([]);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState(null);
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [propertiesData, setPropertiesData] = useState(null);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [propertiesError, setPropertiesError] = useState(null);

  // Parse URL query parameters and load contact if specified
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const contactParam = urlParams.get('contact');
    if (contactParam) {
      setContactId(contactParam);
      loadTimelineData(contactParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch insights for a contact
  const fetchInsights = async (contactId) => {
    if (!contactId.trim()) return;
    
    try {
      setInsightsLoading(true);
      setInsightsError(null);
      
      const response = await apiClient.get(`/analyze/aggregation/contacts/${contactId}/insights`, {
        params: { limit: 50 }
      });
      
      setInsights(response.data || []);
    } catch (err) {
      console.error('Error fetching insights:', err);
      setInsightsError(err.response?.data?.detail || err.message || 'Failed to load insights');
    } finally {
      setInsightsLoading(false);
    }
  };

  // Fetch properties for a contact
  const loadPropertiesData = async (contactId) => {
    if (!contactId) return;
    setPropertiesLoading(true);
    setPropertiesError(null);
    try {
      const response = await apiClient.get(`/timeline/contact/${contactId}/properties`);
      setPropertiesData(response.data);
    } catch (err) {
      console.error('Error loading properties:', err);
      setPropertiesError(err.response?.data?.detail || err.message || 'Failed to load properties');
    } finally {
      setPropertiesLoading(false);
    }
  };

  // Load timeline data
  const loadTimelineData = async (contactId) => {
    if (!contactId.trim()) return;
    
    try {
      setError(null);
      
      const response = await apiClient.get(`/timeline/contact/${contactId}?limit=100`);
      
      if (response.data.success) {
        setTimelineData(response.data);
        // Fetch insights and properties for this contact
        fetchInsights(contactId);
        setPropertiesData(null);
        setPropertiesError(null);
        loadPropertiesData(contactId);
      } else {
        setError(response.data.error || 'Failed to load timeline data');
      }
    } catch (err) {
      console.error('Error loading timeline data:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load timeline data');
    }
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return {
      full: date.toLocaleString(),
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString(),
      relative: getRelativeTime(date)
    };
  };

  // Get relative time (e.g., "2 hours ago")
  const getRelativeTime = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  // Get source-specific styling
  const getSourceStyle = (source, typeId) => {
    const styles = {
      'hubspot_email_events': {
        backgroundColor: '#FF9800',
        color: 'white',
        label: 'HubSpot Email',
        icon: '📧'
      },
      'hubspot_webhook_events': {
        backgroundColor: '#2196F3',
        color: 'white',
        label: 'HubSpot CRM',
        icon: '🔄'
      },
      'esputnik_webhook_events': {
        backgroundColor: '#FF9800',
        color: 'white',
        label: 'eSputnik',
        icon: '📧'
      },
      'userflow_webhook_events': {
        backgroundColor: '#4CAF50',
        color: 'white',
        label: 'Userflow',
        icon: '👤'
      },
      'amplemarket_webhook_events': {
        backgroundColor: '#9C27B0',
        color: 'white',
        label: 'Amplemarket',
        icon: '📊'
      },
      'stripe_webhook_events': {
        backgroundColor: '#635BFF',
        color: 'white',
        label: 'Stripe',
        icon: '💳'
      },
      'customerio': {
        backgroundColor: '#4350E6',
        color: 'white',
        label: 'Customer.io',
        icon: '📧'
      },
      'hubspot': {
        backgroundColor: '#2196F3',
        color: 'white',
        label: 'HubSpot',
        icon: '🔗'
      },
      'esputnik': {
        backgroundColor: '#FF9800',
        color: 'white',
        label: 'eSputnik',
        icon: '🔗'
      },
      'touchflow': {
        backgroundColor: '#7C4DFF',
        color: 'white',
        label: 'TouchFlow',
        icon: '🏷️'
      },
      'default': {
        backgroundColor: '#6c757d',
        color: 'white',
        label: 'Unknown',
        icon: '❓'
      }
    };

    return styles[typeId] || styles[source] || styles.default;
  };

  const formatCtaField = (cta) => {
    if (!cta || !cta?.category) return null;
    return cta?.category 
      + " (" + cta?.confidence + "," 
      + cta?.source +"," 
      + cta?.rationale +"," 
      + cta?.text +"," + 
      cta?.url + ")";
  };

  const formatPrimaryCta = (analysis) => {
    if (!analysis) return null;
    return analysis?.ctas?.filter(cta => cta?.primary).map(cta => formatCtaField(cta)).join(', ');
  };

  const formatGrammar = (analysis) => {
    if (!analysis) return null;
    const issuesCount = analysis?.grammar?.issues?.length;
    return analysis?.grammar?.score + " (" + issuesCount + " issue"+ (issuesCount === 1 ? "" : "s") + ")";
  };

  const formatStage = (analysis) => {
    if (!analysis) return null;
    return analysis?.inferred_stage;
  };

  const formatTone = (analysis) => {
    if (!analysis) return null;
    return analysis?.tone;
  };

  const formatTheme = (analysis) => {
    if (!analysis) return null;
    return analysis?.theme;
  };

  // Process and format event data for display
  const processEventData = (event) => {
    // Handle property_changed events regardless of source
    if (event.event_type === 'property_changed') {
      const changes = event.metadata?.changes || {};
      return {
        ...event,
        title: 'Properties Updated',
        subtitle: event.description || `${Object.keys(changes).length} properties changed`,
        details: changes,
        isPropertyChange: true,
      };
    }
    switch(event.type_id || event.source) {
      case 'hubspot_email_events':
      case 'hubspot_webhook_events':
      case 'hubspot':
        const emailContent = event.content;
        return {
          ...event,
          details: {
            'Subject': emailContent?.subject,
            'Status': emailContent?.status,
            'Direction': emailContent?.direction,
            'Text': emailContent?.text,
            'HTML': emailContent?.html,
            'CTA': event.content?.cta ? formatCtaField(event.content?.cta) : formatPrimaryCta(event.content?.extracted_content),
            'Grammar Score': formatGrammar(event.content?.extracted_content),
            'Stage': formatStage(event.content?.extracted_content),
            'Tone': formatTone(event.content?.extracted_content),
            'Theme': formatTheme(event.content?.extracted_content),
            'Sequence ID': emailContent?.sequence_id,
            'Campaign GUID': emailContent?.campaign_guid,
            'Template ID': emailContent?.template_id,
          }
        };

      case 'esputnik_webhook_events':
      case 'esputnik':
        return {
          ...event,
          details: {
            'Subject': event.content?.subject,
            'Status': event.content?.status,
            'Text': event.content?.text,
            'HTML': event.content?.html,
            'CTA': event.content?.cta ? formatCtaField(event.content?.cta) : formatPrimaryCta(event.content?.extracted_content),
            'Grammar Score': formatGrammar(event.content?.extracted_content),
            'Stage': formatStage(event.content?.extracted_content),
            'Tone': formatTone(event.content?.extracted_content),
            'Theme': formatTheme(event.content?.extracted_content),
            'Media Type': event.content?.mediaType,
            'Message Name': event.content?.messageName,
          }
        };

      case 'customerio_webhook_events':
      case 'customerio':
        return {
          ...event,
          details: {
            'Subject': event.content?.subject,
            'Status': event.content?.status,
            'Text': event.content?.text,
            'CTA': event.content?.cta ? formatCtaField(event.content?.cta) : formatPrimaryCta(event.content),
            'Grammar Score': formatGrammar(event.content),
            'Stage': formatStage(event.content),
            'Tone': formatTone(event.content),
            'Theme': formatTheme(event.content),
            'Content Group': event.content_group?.name,
          }
        };

      case 'userflow_webhook_events':
        return {
          ...event,
          details: {
            'Text': event.content?.text,
            'Type': event.content?.type,
            'ID': event.content?.id
          }
        };
      
      case 'amplemarket_webhook_events':
        return {
          ...event,
          title: event.name || 'Amplemarket Event',
          subtitle: event.description || 'Sales sequence activity',
          details: {
            'Event Type': event.event_type,
            'Sequence': event.metadata?.sequence?.name,
            'Sequence ID': event.metadata?.sequence?.id,
            'Step': event.metadata?.step_name
          }
        };
      
      default:
        return {
          ...event,
          title: event.name || 'Unknown Event',
          subtitle: event.description || 'Event details',
          details: {
            'Event Type': event.event_type,
            'Source': event.source
          }
        };
    }
  };

  // Compute filter options with counts that reflect the other active filters
  const filterOptions = useMemo(() => {
    if (!timelineData?.events) return { typeId: [], eventType: [], contentStatus: [] };

    let base = [...timelineData.events];

    // If insight is selected, narrow the pool first
    if (selectedInsight) {
      const relatedEventIds = getReferencedEventIds(selectedInsight);
      base = base.filter(e => relatedEventIds.has(e.id) || relatedEventIds.has(e.id?.toString()));
    }
    const applyOtherFilters = (events, excludeKey) => {
      let filtered = events;
      if (excludeKey !== 'typeId' && filterTypeId.length > 0)
        filtered = filtered.filter(e => filterTypeId.includes(e.type_id));
      if (excludeKey !== 'eventType' && filterEventType.length > 0)
        filtered = filtered.filter(e => filterEventType.includes(e.event_type));
      if (excludeKey !== 'contentStatus' && filterContentStatus.length > 0)
        filtered = filtered.filter(e => filterContentStatus.includes(e.content?.status));
      return filtered;
    };

    const buildOptions = (events, accessor) => {
      const counts = {};
      events.forEach(e => {
        const val = accessor(e);
        if (val != null && val !== '') {
          counts[val] = (counts[val] || 0) + 1;
        }
      });
      return Object.entries(counts)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([value, count]) => ({ value, count }));
    };

    return {
      typeId: buildOptions(applyOtherFilters(base, 'typeId'), e => e.type_id),
      eventType: buildOptions(applyOtherFilters(base, 'eventType'), e => e.event_type),
      contentStatus: buildOptions(applyOtherFilters(base, 'contentStatus'), e => e.content?.status),
    };
  }, [timelineData, selectedInsight, filterTypeId, filterEventType, filterContentStatus]);

  // Sort and filter events
  const getSortedAndFilteredEvents = useCallback(() => {
    if (!timelineData?.events) return [];

    let events = [...timelineData.events];

    // Filter by selected insight (show only related events)
    if (selectedInsight) {
      const relatedEventIds = getReferencedEventIds(selectedInsight);
      events = events.filter(event => relatedEventIds.has(event.id) || relatedEventIds.has(event.id?.toString()));
    }

    // Multiselect filters
    if (filterTypeId.length > 0) {
      events = events.filter(event => filterTypeId.includes(event.type_id));
    }
    if (filterEventType.length > 0) {
      events = events.filter(event => filterEventType.includes(event.event_type));
    }
    if (filterContentStatus.length > 0) {
      events = events.filter(event => filterContentStatus.includes(event.content?.status));
    }

    // Sort events
    events.sort((a, b) => {
      let aValue, bValue;
      
      switch(sortBy) {
        case 'timestamp':
          aValue = new Date(a.occured_at || a.timestamp);
          bValue = new Date(b.occured_at || b.timestamp);
          break;
        case 'source':
          aValue = a.source;
          bValue = b.source;
          break;
        case 'type':
          aValue = a.event_type;
          bValue = b.event_type;
          break;
        default:
          aValue = new Date(a.occured_at || a.timestamp);
          bValue = new Date(b.occured_at || b.timestamp);
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return events.map(processEventData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelineData, selectedInsight, filterTypeId, filterEventType, filterContentStatus, sortBy, sortOrder]);

  // Get event IDs referenced by an insight
  const getReferencedEventIds = (insight) => {
    const eventIds = new Set();
    
    if (insight.metadata?.issues) {
      insight.metadata.issues.forEach(issue => {
        if (issue.event1_id) eventIds.add(issue.event1_id);
        if (issue.event2_id) eventIds.add(issue.event2_id);
      });
    } else if (insight.metadata?.event1_id && insight.metadata?.event2_id) {
      eventIds.add(insight.metadata.event1_id);
      eventIds.add(insight.metadata.event2_id);
    }

    if (insight.metadata?.conflicting_event_ids) {
      insight.metadata.conflicting_event_ids.forEach(id => eventIds.add(id));
    }

    return eventIds;
  };

  // Handle insight selection
  const handleInsightSelect = (insight) => {
    if (selectedInsight && selectedInsight.id === insight.id) {
      // Deselect if clicking the same insight
      setSelectedInsight(null);
    } else {
      // Select the new insight
      setSelectedInsight(insight);
    }
  };

  const toggleEventExpansion = (eventId) => {
    setExpandedEvents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
      }
      return newSet;
    });
  };

  const events = useMemo(() => getSortedAndFilteredEvents(), [getSortedAndFilteredEvents]);

  // Calculate hidden events count when insight is selected
  const getHiddenEventsCount = () => {
    if (!selectedInsight || !timelineData?.events) return 0;
    const relatedEventIds = getReferencedEventIds(selectedInsight);
    const totalEvents = timelineData.events.length;
    const relatedEvents = timelineData.events.filter(event => 
      relatedEventIds.has(event.id) || relatedEventIds.has(event.id?.toString())
    ).length;
    return totalEvents - relatedEvents;
  };
  
  const hiddenEventsCount = getHiddenEventsCount();

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ color: '#2c3e50', marginBottom: '8px', fontSize: '28px' }}>
          Contact Timeline - List View
        </h1>
        <p style={{ color: '#6c757d', fontSize: '16px', margin: '0 0 20px 0' }}>
          Detailed chronological view of all contact interactions and events
        </p>
        
        {/* Status Messages */}
        {error && (
          <div style={{ 
            padding: '12px', 
            backgroundColor: '#f8d7da', 
            color: '#721c24', 
            border: '1px solid #f5c6cb',
            borderRadius: '6px',
            marginBottom: '20px'
          }}>
            <strong>Error:</strong> {error}
          </div>
        )}
        
        {timelineData && (
          <div style={{ 
            padding: '12px', 
            backgroundColor: '#d1ecf1', 
            color: '#0c5460', 
            border: '1px solid #bee5eb',
            borderRadius: '6px',
            marginBottom: '20px'
          }}>
            <strong>Timeline loaded for:</strong> {timelineData.contact_email}
            <span style={{ marginLeft: '10px', fontSize: '14px' }}>
              ({timelineData.events_count} events
              {propertiesData && ` · ${Object.keys(propertiesData.properties || {}).length} properties`})
            </span>
          </div>
        )}
      </div>

      {/* Controls */}
      {timelineData && (
        <div style={{ 
          marginBottom: '20px', 
          padding: '15px', 
          backgroundColor: '#f8f9fa', 
          borderRadius: '6px',
          border: '1px solid #e9ecef'
        }}>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ fontSize: '14px', color: '#6c757d', display: 'flex', alignItems: 'center', gap: '12px' }}>
              {selectedInsight && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '4px 8px',
                  backgroundColor: '#dbeafe',
                  color: '#1d4ed8',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '500'
                }}>
                  <span>🔍</span>
                  Filtered by insight
                  <button
                    onClick={() => setSelectedInsight(null)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#1d4ed8',
                      cursor: 'pointer',
                      padding: '0',
                      marginLeft: '4px',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    ×
                  </button>
                </div>
              )}
              <span>
                {selectedInsight 
                  ? `Showing ${events.length} related events (${hiddenEventsCount} hidden)`
                  : `Showing ${events.length} of ${timelineData.events_count} events`
                }
              </span>
            </div>
          </div>

          {/* Multiselect Filters Row */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 250px', minWidth: 0 }}>
              <label style={{ fontSize: '14px', fontWeight: '500', whiteSpace: 'nowrap' }}>Source:</label>
              <Select
                mode="multiple"
                allowClear
                placeholder="All"
                value={filterTypeId}
                onChange={(values) => setFilterTypeId(values.includes('__all__') ? [] : values)}
                style={{ width: '100%' }}
                maxTagCount="responsive"
                options={[
                  { value: '__all__', label: 'All' },
                  ...filterOptions.typeId.map(o => ({
                    value: o.value,
                    label: <span>{o.value} <Tag style={{ marginLeft: 4 }}>{o.count}</Tag></span>,
                  })),
                ]}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '2 1 300px', minWidth: 0 }}>
              <label style={{ fontSize: '14px', fontWeight: '500', whiteSpace: 'nowrap' }}>Event Type:</label>
              <Select
                mode="multiple"
                allowClear
                placeholder="All"
                value={filterEventType}
                onChange={(values) => setFilterEventType(values.includes('__all__') ? [] : values)}
                style={{ width: '100%' }}
                maxTagCount="responsive"
                options={[
                  { value: '__all__', label: 'All' },
                  ...filterOptions.eventType.map(o => ({
                    value: o.value,
                    label: <span>{o.value.replace(/[^a-zA-Z0-9]+/g, ' ').trim().toLowerCase().replace(/\b\w/g, c => c.toUpperCase())} <Tag style={{ marginLeft: 4 }}>{o.count}</Tag></span>,
                  })),
                ]}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 250px', minWidth: 0 }}>
              <label style={{ fontSize: '14px', fontWeight: '500', whiteSpace: 'nowrap' }}>Status:</label>
              <Select
                mode="multiple"
                allowClear
                placeholder="All"
                value={filterContentStatus}
                onChange={(values) => setFilterContentStatus(values.includes('__all__') ? [] : values)}
                style={{ width: '100%' }}
                maxTagCount="responsive"
                options={[
                  { value: '__all__', label: 'All' },
                  ...filterOptions.contentStatus.map(o => ({
                    value: o.value,
                    label: <span>{o.value} <Tag style={{ marginLeft: 4 }}>{o.count}</Tag></span>,
                  })),
                ]}
              />
            </div>
          </div>
        </div>
      )}

      {/* Main Content - Two Column Layout */}
      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
        {/* Left Column - Timeline Events List */}
        <div style={{ flex: '2', minWidth: '0' }}>
          {events.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {events.map((event, index) => {
            const sourceStyle = getSourceStyle(event.source, event.type_id);
            const timestamp = formatTimestamp(event.occured_at || event.timestamp);
            const eventId = event.id || `${event.source}-${index}`;
            const isExpanded = expandedEvents.has(eventId);
            
            return (
              <div
                key={event.id || index}
                id={`event-${event.id || index}`}
                style={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  padding: '0',
                  //boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  //transition: 'box-shadow 0.2s ease',
                  overflow: 'hidden'
                }}
                //onMouseEnter={(e) => e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'}
                //onMouseLeave={(e) => e.target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)'}
              >
                {/* Header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  borderBottom: '1px solid #f3f4f6'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {/* Icon */}
                    <div style={{
                      width: '32px',
                      height: '32px',
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <span style={{ fontSize: '32px' }}>{sourceStyle.icon}</span>
                    </div>

                    {/* Service Name and Status */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#111827'
                      }}>
                        {sourceStyle.label}
                      </div>
                      <div style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        backgroundColor: event.isPropertyChange ? '#f3e8ff' : '#dbeafe',
                        color: event.isPropertyChange ? '#6d28d9' : '#1d4ed8',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {event.event_type.replace(/[^a-zA-Z0-9]+/g, ' ').trim().toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) || 'Occurred'}
                      </div>
                    </div>
                  </div>

                  {/* Timestamp and Actions */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      textAlign: 'right',
                      fontSize: '14px',
                      color: '#6b7280'
                    }}>
                      {timestamp.full}
                    </div>
                    {!event.isPropertyChange && (
                      <div style={{ display: 'flex', gap: '8px' }}>
                       <button
                         onClick={() => toggleEventExpansion(eventId)}
                         style={{
                           background: 'none',
                           border: 'none',
                           cursor: 'pointer',
                           padding: '4px 8px',
                           borderRadius: '4px',
                           color: '#3b82f6',
                           fontSize: '14px',
                           fontWeight: '500'
                         }}
                       >
                         {isExpanded ? 'See Less' : 'See More'}
                       </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Property Change Content */}
                {event.isPropertyChange && (
                  <div style={{ padding: '12px 16px' }}>
                    {event.metadata?.triggered_by?.type_id && (
                      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                        Triggered by <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{event.metadata.triggered_by.type_id}</span>
                      </div>
                    )}
                    {(event.content_group?.name || event.content?.text) ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        {event.content_group?.name && (
                          <div style={{
                            fontSize: '14px',
                            fontWeight: 600,
                            color: '#5B21B6',
                          }}>
                            {event.content_group.name}
                          </div>
                        )}
                        {event.content?.text && (
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '4px 10px',
                            backgroundColor: '#f5f0ff',
                            border: '1px solid #d8b4fe',
                            borderRadius: '6px',
                            fontSize: '13px',
                            color: '#111827',
                          }}>
                            {event.content.text}
                          </div>
                        )}
                      </div>
                    ) : event.description && (
                      <div style={{ fontSize: '13px', color: '#374151' }}>
                        {event.description}
                      </div>
                    )}
                  </div>
                )}

                {/* Regular Event Content */}
                {!event.isPropertyChange && (<>
                <div style={{ padding: '16px' }}>
                  {/* Content Group ID & External Link (one line) */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px'
                  }}>
                    {(event.content_group?.name && event.content_group?.description) && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>
                          {event.content_group.description}: {event.content_group.name}
                        </span>
                        {timelineData.content_external_links && timelineData.content_external_links[event.content_group?.id] && (
                          <a href={timelineData.content_external_links[event.content_group.id]} target="_blank" rel="noopener noreferrer">
                            <span style={{ fontSize: '16px' }}>🔗</span>
                          </a>
                        )}
                      </div>
                    )}
                    
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flexWrap: 'wrap',
                      background: 'none',
                      borderRadius: '8px',
                      padding: '2px 4px',
                      border: 'none'
                    }}>
                    {/* Grammar Score */}
                    {event.content?.extracted_content?.grammar && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: '#e0f2fe',
                        color: '#0369a1',
                        borderRadius: '4px',
                        padding: '4px 10px',
                        fontWeight: 500,
                        fontSize: '13px'
                      }}>
                        <span style={{fontSize: '15px'}}>📝</span>
                        <span>Grammar: {event.content?.extracted_content?.grammar?.score}</span>
                      </div>
                    )}

                    {/* Tone */}
                    {event.content?.extracted_content?.tone && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: '#fef9c3',
                        color: '#b45309',
                        borderRadius: '4px',
                        padding: '4px 10px',
                        fontWeight: 500,
                        fontSize: '13px'
                      }}>
                        <span style={{fontSize: '15px'}}>🎵</span>
                        <span>Tone: {event.content?.extracted_content?.tone}</span>
                      </div>
                    )}

                    {/* Stage */}
                    {event.content?.extracted_content?.stage && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: '#dcfce7',
                        color: '#15803d',
                        borderRadius: '4px',
                        padding: '4px 10px',
                        fontWeight: 500,
                        fontSize: '13px'
                      }}>
                        <span style={{fontSize: '15px'}}>🏁</span>
                        <span>Stage: {event.content?.extracted_content?.stage}</span>
                      </div>
                    )}

                    {/* CTA */}
                    {(() => {
                      // Try to extract CTA object from event.content or event.details
                      let cta = event.content?.cta;
                      if (event.content?.ctas) {
                        cta = event.content.ctas.find(c => c.primary && c.category !== 'other');
                      }
                      if (cta && (cta.text || cta.url)) {
                        // Color code by confidence
                        let bg = '#f3f4f6', color = '#374151';
                        if (cta.confidence >= 0.8 || cta.confidence === 'high') {
                          bg = '#d1fae5'; color = '#065f46';
                        } else if (cta.confidence >= 0.5 || cta.confidence === 'medium') {
                          bg = '#fef9c3'; color = '#92400e';
                        } else if (cta.confidence) {
                          bg = '#fee2e2'; color = '#991b1b';
                        }
                        return (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: bg,
                            color: color,
                            borderRadius: '4px',
                            padding: '4px 10px',
                            fontWeight: 500,
                            fontSize: '13px'
                          }}>
                            <span style={{fontSize: '12px'}}>
                              CTA: ({cta.category
                                ? cta.category.replaceAll('_', ' ').replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                                : ''})
                            </span>
                            <a
                              href={cta.url || cta.link || '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: color,
                                textDecoration: 'underline',
                                fontWeight: 600,
                                fontSize: '13px'
                              }}
                            >
                              {cta.text || 'CTA Link'}
                            </a>
                            {/*{cta.confidence && (
                              <span style={{
                                fontSize: '12px',
                                background: 'rgba(0,0,0,0.04)',
                                borderRadius: '2px',
                                padding: '1px 5px',
                                marginLeft: '4px'
                              }}>
                                {typeof cta.confidence === 'number'
                                  ? `Conf: ${(cta.confidence * 100).toFixed(0)}%`
                                  : `Conf: ${cta.confidence}`}
                              </span>
                            )}*/}
                          </div>
                        );
                      }
                      return null;
                    })()}

                    {/* Theme */}
                    {event.content?.extracted_content?.theme && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: '#ede9fe',
                        color: '#6d28d9',
                        borderRadius: '4px',
                        padding: '4px 10px',
                        fontWeight: 500,
                        fontSize: '13px'
                      }}>
                        <span style={{fontSize: '15px'}}>🎨</span>
                        <span>Theme: {event.content?.extracted_content?.theme}</span>
                      </div>
                    )}
                  </div>
                  </div>

                  {/* Email Subject */}
                  {isExpanded && (
                  <h2 style={{
                    fontSize: '14px',
                    fontWeight: '700',
                    color: '#6b7280',
                    margin: '0 0 2px 0',
                    lineHeight: '1.2'
                  }}>
                    {event.content?.subject || event.description}
                  </h2>
                  )}
                  {/* Email Preview Markdown */}
                  {isExpanded && (
                  <p style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    margin: '0 0 4px 0',
                    lineHeight: '1.5'
                  }}>
                    <Markdown 
                      options={{
                        skipHtml: true,
                      }}
                    >{event.content?.text || event.details?.Text || 'No Content available'}
                    </Markdown>
                  </p>
                  )}
                </div>
                {/* Grammar Section */}
                {isExpanded && event.content?.extracted_content?.grammar && event.content?.extracted_content?.grammar?.issues?.length > 0 && (
                  <div style={{ padding: '0 20px 20px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>Grammar</h3>
                    <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>{event.content?.extracted_content?.grammar?.issues?.length} issues found</p>
                    <ul style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
                      {event.content?.extracted_content?.grammar?.issues?.sort((a, b) => a.severity - b.severity).map((issue, index) => (
                        <li key={index} style={{ fontStyle: 'italic', color: '#374151', borderLeft: '3px solid #3b82f6', paddingLeft: '10px', marginBottom: '8px', background: '#f1f5f9', borderRadius: '4px' }}>
                          <span style={{ fontWeight: 'bold', color: '#1e40af' }}>{issue.type}</span>
                          <span style={{ margin: '0 6px', color: '#6b7280' }}>–</span>
                          <span style={{ color: '#374151' }}>{issue.description}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Raw Event Data - Only show when expanded */}
                {isExpanded && event.metadata && Object.keys(event.metadata).length > 0 && (
                  <div style={{ padding: '0 20px 20px' }}>
                    <details style={{ fontSize: '12px' }}>
                      <summary style={{ 
                        cursor: 'pointer', 
                        color: '#3b82f6',
                        fontWeight: '500',
                        marginBottom: '8px'
                      }}>
                        View Raw Metadata
                      </summary>
                      <pre style={{ 
                        backgroundColor: '#f3f4f6', 
                        padding: '10px', 
                        borderRadius: '4px',
                        overflow: 'auto',
                        fontSize: '11px',
                        lineHeight: '1.4',
                        color: '#374151'
                      }}>
                        {JSON.stringify(event, null, 2)}
                      </pre>
                    </details>
                  </div>
                )}
                </>)}
              </div>
            );
          })}
            </div>
          )}

          {/* Hidden Events Indicator */}
          {selectedInsight && hiddenEventsCount > 0 && (
            <div style={{
              marginTop: '24px',
              padding: '16px',
              backgroundColor: '#f8fafc',
              border: '2px dashed #cbd5e1',
              borderRadius: '12px',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onClick={() => setSelectedInsight(null)}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f1f5f9';
              e.target.style.borderColor = '#94a3b8';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#f8fafc';
              e.target.style.borderColor = '#cbd5e1';
            }}
            >
              <div style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#475569',
                marginBottom: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '18px' }}>📦</span>
                {hiddenEventsCount} Hidden Event{hiddenEventsCount !== 1 ? 's' : ''}
              </div>
              <div style={{
                fontSize: '14px',
                color: '#64748b',
                marginBottom: '8px'
              }}>
                {hiddenEventsCount} event{hiddenEventsCount !== 1 ? 's are' : ' is'} not related to the selected insight
              </div>
              <div style={{
                fontSize: '13px',
                color: '#3b82f6',
                fontWeight: '500'
              }}>
                Click here to show all events
              </div>
            </div>
          )}

          {/* Empty State */}
          {timelineData && events.length === 0 && (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e9ecef'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>
                {selectedInsight ? '🔍' : '📭'}
              </div>
              <h3 style={{ color: '#6c757d', margin: '0 0 8px 0' }}>
                {selectedInsight ? 'No Related Events Found' : 'No Events Found'}
              </h3>
              <p style={{ color: '#6c757d', margin: '0' }}>
                {selectedInsight 
                  ? `No events are directly related to the selected insight. Click "Show all events" to see the full timeline.`
                  : (filterTypeId.length > 0 || filterEventType.length > 0 || filterContentStatus.length > 0)
                    ? `No events found for the selected filters.`
                    : `No timeline events found for this contact.`
                }
              </p>
              {selectedInsight && (
                <button
                  onClick={() => setSelectedInsight(null)}
                  style={{
                    marginTop: '16px',
                    padding: '8px 16px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Show All Events
                </button>
              )}
            </div>
          )}
        </div>

        {/* Right Column - Properties & Insights */}
        {timelineData && (
          <div style={{ flex: '1', minWidth: '300px', maxWidth: '400px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Contact Properties */}
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              padding: '20px',
            }}>
              <h2 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#111827',
                margin: '0 0 12px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '20px' }}>🏷️</span>
                Contact Properties
              </h2>

              {propertiesLoading && (
                <div style={{ textAlign: 'center', padding: '16px', color: '#6b7280' }}>
                  Loading properties...
                </div>
              )}

              {propertiesError && (
                <div style={{
                  padding: '10px',
                  backgroundColor: '#fef2f2',
                  color: '#991b1b',
                  border: '1px solid #fecaca',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}>
                  {propertiesError}
                </div>
              )}

              {propertiesData && !propertiesLoading && (
                <div>
                  {/* Current Properties */}
                  {Object.keys(propertiesData.properties || {}).length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {Object.entries(propertiesData.properties).map(([key, property]) => (
                        <div key={key} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '6px 10px',
                          backgroundColor: '#f9fafb',
                          borderRadius: '6px',
                          border: '1px solid #f3f4f6',
                          fontSize: '13px',
                        }}>
                          <span style={{ color: '#6b7280', fontWeight: 500 }}>{property.label}</span>
                          <span style={{ color: '#111827', fontWeight: 600, textAlign: 'right', maxWidth: '55%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {property.data_type === 'date' ? new Date(property.value).toLocaleString() : String(property.value ?? '')}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ color: '#6b7280', fontSize: '13px', textAlign: 'center', padding: '8px' }}>
                      No properties found.
                    </div>
                  )}

                  {/* Change History Summary */}
                  {propertiesData.history && propertiesData.history.length > 0 && (
                    <div style={{ marginTop: '16px' }}>
                      <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                        Change History ({propertiesData.history.length})
                      </h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '200px', overflowY: 'auto' }}>
                        {propertiesData.history.map((entry, idx) => (
                          <div key={idx} style={{
                            padding: '8px 10px',
                            backgroundColor: '#faf7ff',
                            border: '1px solid #ede9fe',
                            borderRadius: '6px',
                            fontSize: '12px',
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                              <span style={{
                                padding: '1px 6px',
                                backgroundColor: '#ede9fe',
                                color: '#6d28d9',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: 500,
                              }}>
                                {entry.source}
                              </span>
                              <span style={{ color: '#9ca3af', fontSize: '11px' }}>
                                {new Date(entry.timestamp).toLocaleString()}
                              </span>
                            </div>
                            <div style={{ color: '#374151' }}>
                              {Object.entries(entry.changes).map(([k, v]) => (
                                <span key={k} style={{ marginRight: '8px' }}>
                                  <span style={{ fontWeight: 500 }}>{k}</span>: {String(v ?? '')}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {!propertiesData && !propertiesLoading && !propertiesError && (
                <div style={{ color: '#6b7280', fontSize: '13px', textAlign: 'center', padding: '8px' }}>
                  Select a contact to view properties.
                </div>
              )}
            </div>

            {/* Contact Lifecycles */}
            {contactId && (
              <div style={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                padding: '20px',
              }}>
                <h2 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#111827',
                  margin: '0 0 12px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                  Lifecycles
                </h2>
                <ContactLifecycleView contactId={contactId} />
              </div>
            )}

            {/* Contact Insights */}
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              padding: '20px',
              position: 'sticky',
              top: '20px'
            }}>
              <h2 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#111827',
                margin: '0 0 8px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '20px' }}>💡</span>
                Contact Insights
              </h2>
              
              {insights.length > 0 && (
                <p style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  margin: '0 0 16px 0',
                  fontStyle: 'italic'
                }}>
                  Click an insight to filter events and focus on related communications
                </p>
              )}

              {insightsLoading && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '20px',
                  color: '#6b7280'
                }}>
                  Loading insights...
                </div>
              )}

              {insightsError && (
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: '#fef2f2', 
                  color: '#991b1b', 
                  border: '1px solid #fecaca',
                  borderRadius: '6px',
                  marginBottom: '16px',
                  fontSize: '14px'
                }}>
                  <strong>Error:</strong> {insightsError}
                </div>
              )}

              {!insightsLoading && !insightsError && insights.length === 0 && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '20px',
                  color: '#6b7280',
                  fontSize: '14px'
                }}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>🔍</div>
                  No insights available for this contact.
                </div>
              )}

              {!insightsLoading && insights.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {insights.map((insight, index) => {
                    const isSelected = selectedInsight && selectedInsight.id === insight.id;
                    
                    return (
                      <div
                        key={insight.id || index}
                        onClick={() => handleInsightSelect(insight)}
                        style={{
                          padding: '8px 10px',
                          backgroundColor: isSelected ? '#eff6ff' : '#f9fafb',
                          border: isSelected ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                          borderRadius: '6px',
                          fontSize: '13px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          position: 'relative'
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.target.style.backgroundColor = '#f3f4f6';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.target.style.backgroundColor = '#f9fafb';
                          }
                        }}
                      >
                      {/* Insight Header */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start',
                        marginBottom: '4px'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          {isSelected && (
                            <span style={{ fontSize: '12px', color: '#3b82f6' }}>🎯</span>
                          )}
                          <div style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: isSelected ? '#1d4ed8' : '#374151',
                            textTransform: 'capitalize'
                          }}>
                            {insight.insight_type?.replace(/_/g, ' ')}
                          </div>
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          {insight.severity && (
                            <div style={{
                              fontSize: '11px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              backgroundColor: insight.severity === 'High' ? '#fee2e2' : 
                                             insight.severity === 'Medium' ? '#fef3c7' : '#f0fdf4',
                              color: insight.severity === 'High' ? '#991b1b' : 
                                     insight.severity === 'Medium' ? '#92400e' : '#166534'
                            }}>
                              {insight.severity}
                            </div>
                          )}
                          {insight.processor_version && (
                            <div style={{
                              fontSize: '11px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              backgroundColor: insight.processor_version === '1.0.0' || insight.processor_version === 'default' ? '#e5e7eb' : 
                                               insight.processor_version === 'short' ? '#fef3c7' : '#f0fdf4',
                              color: insight.processor_version === '1.0.0' || insight.processor_version === 'default' ? '#991b1b' : 
                                     insight.processor_version === 'short' ? '#92400e' : '#166534'
                            }}>
                              {insight.processor_version === '1.0.0' || insight.processor_version === 'default' ? 'Default' : insight.processor_version}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Insight Title */}
                      {insight.title && (
                        <div style={{
                          fontWeight: '500',
                          color: '#111827',
                          marginBottom: '6px',
                          fontSize: '13px'
                        }}>
                          {insight.title}
                        </div>
                      )}

                      {/* Insight Summary */}
                      {insight.description && (
                        <div style={{
                          color: '#6b7280',
                          lineHeight: '1.4',
                          fontSize: '11px'
                        }}>
                          {insight.description}
                        </div>
                      )}

                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TimelineListPage; 